import { CircularProgress, Box, CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { postAuthPath } from "./auth/postAuthPath";
import AnalyticsProvider from "./analytics/AnalyticsProvider";
import AppLayout from "./components/AppLayout";
import CookieConsent from "./components/CookieConsent";
import { OnboardingProvider } from "./onboarding/OnboardingContext";
import OnboardingCoachmark from "./onboarding/OnboardingCoachmark";
import OnboardingEffects from "./onboarding/OnboardingEffects";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AuthCompletePage = lazy(() => import("./pages/AuthCompletePage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const TimetablePage = lazy(() => import("./pages/TimetablePage"));
const LessonPlanningPage = lazy(() => import("./pages/LessonPlanningPage"));
const SchemeRoute = lazy(() => import("./pages/SchemeRoute"));

const theme = createTheme({
  palette: {
    primary: { main: "#0d47a1" },
    secondary: { main: "#00838f" },
    background: { default: "#f5f7fa" },
  },
  typography: {
    fontFamily: '"Source Sans 3", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h4: {
      fontFamily: '"Libre Baskerville", Georgia, serif',
      "@media (max-width: 599.95px)": { fontSize: "1.875rem" },
    },
    h6: { fontFamily: '"Libre Baskerville", Georgia, serif' },
  },
  shape: { borderRadius: 8 },
});

function PageLoading() {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <CircularProgress />
    </Box>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (user) return <Navigate to={postAuthPath("returning")} replace />;
  return children;
}

function Fallback() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, "") || "/";
  if (loading) return <PageLoading />;
  if (path === "/auth/complete") return <AuthCompletePage />;
  if (path === "/register") {
    return (
      <PublicOnly>
        <RegisterPage />
      </PublicOnly>
    );
  }
  if (path === "/login" || path === "/") {
    return (
      <PublicOnly>
        <LoginPage />
      </PublicOnly>
    );
  }
  return <Navigate to={user ? postAuthPath("returning") : "/login"} replace />;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <OnboardingProvider>
              <AnalyticsProvider>
                <Suspense fallback={<PageLoading />}>
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        <PublicOnly>
                          <LoginPage />
                        </PublicOnly>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <PublicOnly>
                          <RegisterPage />
                        </PublicOnly>
                      }
                    />
                    <Route path="/auth/complete" element={<AuthCompletePage />} />
                    <Route
                      path="/"
                      element={
                        <PublicOnly>
                          <LoginPage />
                        </PublicOnly>
                      }
                    />
                    <Route
                      element={
                        <Protected>
                          <AppLayout />
                        </Protected>
                      }
                    >
                      <Route path="teach" element={<HomePage />} />
                      <Route path="home" element={<Navigate to={postAuthPath("returning")} replace />} />
                      <Route path="calendar" element={<CalendarPage />} />
                      <Route path="timetable" element={<TimetablePage />} />
                      <Route path="planning" element={<LessonPlanningPage />} />
                      <Route path="scheme" element={<SchemeRoute />} />
                    </Route>
                    <Route path="*" element={<Fallback />} />
                  </Routes>
                </Suspense>
                <OnboardingEffects />
                <OnboardingCoachmark />
                <CookieConsent />
              </AnalyticsProvider>
            </OnboardingProvider>
          </BrowserRouter>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
